import { useEffect, useRef, useState } from 'react';
import { ASSISTANT_NAME } from '../lib/assistant';
import { VagdeviMark } from './VagdeviMark';
import { Link } from 'react-router-dom';
import { onEveryWelcomeEnd, whenWelcomeOver } from '../lib/welcome-sequence';
import { aiAvailable } from '../lib/ai-availability';

/**
 * Whether opening the panel now would cover the page: at 640px and below
 * styles.css turns it into a full-width sheet. Kept in step with AiLauncher.
 */
function panelWouldCoverPage(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
}

/**
 * What the assistant looks like to a visitor who has not signed in.
 *
 * The catalogue greets every arrival out loud, and the greeting introduces
 * the assistant: "I'm your AI Learning Assistant. I can help you understand
 * course concepts…". Most people who hear that are signed out, and the real
 * assistant cannot serve them — every question is answered from one course's
 * material and checked against an entitlement, so there is nothing it could
 * say without an account behind the question.
 *
 * That left two honest options: say nothing on screen while a voice describes
 * an assistant that is not there, or show the assistant introducing itself
 * and say plainly what it needs. This is the second.
 *
 * It deliberately has **no question box**. An input that accepts a question
 * and then refuses it is worse than no input: the refusal arrives after the
 * effort of typing, and it reads as a fault rather than a sign-in prompt.
 *
 * It is a separate component from AiLauncher rather than a mode of it. The
 * launcher's job is choosing a course and holding a conversation, none of
 * which applies here, and threading "signed out" through that state machine
 * bought nothing but a wider one.
 */
export function AiInvitation() {
  /** Closed, open, or minimised — the same three states as the real panel. */
  const [mode, setMode] = useState<'closed' | 'open' | 'min'>('closed');
  // Unknown until the server says; nothing is shown before then, and nothing
  // at all on a deployment with the assistant switched off — inviting someone
  // to sign in for an assistant that will not be there is the one thing this
  // panel must not do.
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void aiAvailable().then((on) => {
      if (!cancelled) setAvailable(on);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const open = mode === 'open';
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Open once the spoken welcome is over, so the assistant's portrait has
  // finished speaking and is stepping aside as this arrives.
  //
  // Nothing here is guarded against running twice, and that is deliberate.
  // React runs effects twice in development — mount, clean up, mount again —
  // and an earlier version skipped the second run because a ref said it had
  // already opened, by which time the cleanup had cancelled the thing that
  // was going to open it. Opening an already-open panel is a no-op, so the
  // guard bought nothing and cost the ordering.
  //
  // Except on a phone, where the panel is a sheet over the whole page and
  // opening it unprompted hid the course — Enrol button and all — behind an
  // invitation nobody asked for. There the bubble waits to be pressed. The
  // same rule, for the same reason, as AiLauncher.
  useEffect(() => {
    return whenWelcomeOver(() => {
      if (panelWouldCoverPage()) return;
      setMode((value) => (value === 'closed' ? 'open' : value));
    });
  }, []);

  // And after every later welcome, when the visitor comes back to the
  // catalogue with this panel still mounted: she speaks with the chat hidden,
  // and it opens as she leaves (on a phone, it is left as it was).
  useEffect(
    () =>
      onEveryWelcomeEnd(() => {
        if (panelWouldCoverPage()) return;
        setMode('open');
      }),
    [],
  );

  // Reserve the strip the fixed bubble would otherwise cover at the foot of
  // a scrolled catalogue.
  useEffect(() => {
    if (!available) return;
    document.body.dataset.sxLauncher = 'on';
    return () => {
      delete document.body.dataset.sxLauncher;
    };
  }, [available]);

  // Escape puts it away rather than ending it, as in the real panel.
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setMode('min');
      buttonRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!available) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`ailaunch${open ? ' ailaunch--on' : ''}`}
        onClick={() => setMode((value) => (value === 'open' ? 'min' : 'open'))}
        aria-expanded={open}
        aria-controls="sx-ai-invite"
        aria-label={open ? `Minimise ${ASSISTANT_NAME}` : `About ${ASSISTANT_NAME}`}
        title={open ? 'Minimise' : `About ${ASSISTANT_NAME}`}
      >
        <span className="ailaunch__icon" aria-hidden="true">
          {open ? '⌄' : <VagdeviMark size={34} />}
        </span>
        <span className="ailaunch__label">
          <span className="ailaunch__name">{ASSISTANT_NAME}</span>
          {/* What pressing it does. The name alone says who, not what. */}
          <span className="ailaunch__hint">Ask AI</span>
        </span>
      </button>

      {mode !== 'closed' ? (
        <div
          id="sx-ai-invite"
          className={`aidock${mode === 'min' ? ' aidock--min' : ''}`}
          role="dialog"
          aria-modal="false"
          aria-label={`About ${ASSISTANT_NAME}`}
          aria-hidden={mode === 'min'}
          tabIndex={-1}
          ref={panelRef}
        >
          <header className="aidock__head">
            <div className="aidock__who">
              <p className="aidock__title">
                <VagdeviMark size={20} /> {ASSISTANT_NAME}
              </p>
              <p className="aidock__ctx">Your AI Learning Assistant</p>
            </div>
            <div className="aidock__acts">
              <button
                type="button"
                className="iconbtn"
                aria-label="Minimise"
                title="Minimise"
                onClick={() => {
                  setMode('min');
                  buttonRef.current?.focus();
                }}
              >
                —
              </button>
              <button
                type="button"
                className="iconbtn"
                aria-label="Close"
                title="Close"
                onClick={() => {
                  setMode('closed');
                  buttonRef.current?.focus();
                }}
              >
                ✕
              </button>
            </div>
          </header>

          <div className="aidock__pick">
            {/* The same words the greeting says, so hearing it and reading it
                agree. */}
            <p className="aidock__picknote">
              Welcome to SkillXzen. I can help you understand course concepts, answer questions,
              explain lessons, and guide you through your learning journey.
            </p>
            <p className="muted">
              Answers come from the material of a course you are enrolled on, so sign in and I can
              start answering.
            </p>
            <div className="aidock__acts">
              <Link className="btn btn--sm" to="/login">
                Sign in
              </Link>
              <Link className="btn btn--outline btn--sm" to="/register">
                Create account
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
